import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import { X, Save, AlertCircle, Eye, EyeOff, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #111827;
  font-size: 1.5rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid ${props => props.error ? '#ef4444' : '#d1d5db'};
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: ${props => props.error ? '#ef4444' : '#3b82f6'};
    box-shadow: 0 0 0 3px ${props => props.error ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  }

  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: border-color 0.2s;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }
`;

const ToggleSection = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  overflow: hidden;
`;

const ToggleHeader = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #f9fafb;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const ToggleTitle = styled.div`
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
`;

const ToggleContent = styled.div`
  padding: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  
  &:hover {
    color: #374151;
  }
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 48px;
  height: 24px;
`;

const ToggleInput = styled.input`
  opacity: 0;
  width: 0;
  height: 0;
  
  &:checked + span {
    background-color: #10b981;
  }
  
  &:checked + span:before {
    transform: translateX(24px);
  }
`;

const Slider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #d1d5db;
  transition: 0.3s;
  border-radius: 24px;
  
  &:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
  }
`;

const ActiveToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 8px;
  margin-bottom: 1.5rem;
`;

const ColorPickerWrapper = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const ColorInput = styled.input`
  width: 80px;
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  
  &::-webkit-color-swatch-wrapper {
    padding: 2px;
  }
  
  &::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
  }
`;

const RandomColorButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #374151;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
  }
`;

const ColorPreview = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${props => props.color};
  border: 2px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
`;

const ErrorMessage = styled.div`
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #dc2626;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FieldError = styled.div`
  margin-top: 0.25rem;
  color: #ef4444;
  font-size: 0.875rem;
`;

const HelpText = styled.div`
  margin-top: 0.25rem;
  color: #6b7280;
  font-size: 0.875rem;
`;

const WarningText = styled.div`
  padding: 0.75rem;
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 6px;
  color: #92400e;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.9rem;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
  
  &:hover:not(:disabled) {
    background: #f3f4f6;
  }
`;

const SaveButton = styled(Button)`
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  border: none;
  color: white;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }
`;

const AVATAR_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#ec4899', '#8b5cf6', 
  '#ef4444', '#06b6d4', '#84cc16', '#f97316', '#6366f1'
];

const ROLES = [
  { value: 'case_agent', label: 'Case Agent' },
  { value: 'lead', label: 'Lead' },
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'client', label: 'Client' },
];

function EditUserModal({ isOpen, onClose, user, currentUserId, onSave, loading = false }) {
  const [formData, setFormData] = useState({
    email: '',
    full_name: '',
    role: '',
    avatar_color: '',
    is_active: true,
    password: '',
    confirmPassword: ''
  });

  const [errors, setErrors] = useState({});
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [apiError, setApiError] = useState('');

  const isEditingSelf = user?.id === currentUserId;

  useEffect(() => {
    if (isOpen && user) {
      setFormData({
        email: user.email || '',
        full_name: user.full_name || '',
        role: user.role || 'case_agent',
        avatar_color: user.avatar_color || '#3b82f6',
        is_active: user.is_active,
        password: '',
        confirmPassword: ''
      });
      setErrors({});
      setShowPasswordSection(false);
      setShowPassword(false);
      setShowConfirmPassword(false);
      setApiError('');
    }
  }, [isOpen, user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    setApiError('');
  };

  const generateRandomColor = () => {
    const randomColor = AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];
    handleChange('avatar_color', randomColor);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Password validation only if changing password
    if (showPasswordSection && formData.password) {
      if (formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters';
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^a-zA-Z0-9])/.test(formData.password)) {
        newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
      }

      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const updates = {
        email: formData.email.trim() || null,
        full_name: formData.full_name.trim(),
        role: formData.role,
        avatar_color: formData.avatar_color,
        is_active: formData.is_active
      };

      // Only include password if changing it
      if (showPasswordSection && formData.password) {
        updates.password = formData.password;
      }

      await onSave(user.id, updates);
      onClose();
    } catch (error) {
      // Handle FastAPI validation errors (array of error objects)
      let errorMessage = 'Failed to update user';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        
        // If detail is an array (Pydantic validation errors)
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } 
        // If detail is a string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // If detail is an object
        else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setApiError(errorMessage);
    }
  };

  const getInitials = () => {
    if (!formData.full_name) return '?';
    return formData.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  if (!isOpen || !user) return null;

  const modalContent = (
    <ModalOverlay>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Edit User: {user.username}</ModalTitle>
          <CloseButton onClick={onClose} disabled={loading}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>

        {apiError && (
          <ErrorMessage>
            <AlertCircle size={16} />
            {apiError}
          </ErrorMessage>
        )}

        {isEditingSelf && (
          <WarningText>
            <AlertCircle size={16} />
            You are editing your own account. Be careful with role changes.
          </WarningText>
        )}

        <FormGroup>
          <Label>Email</Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="user@example.com"
            error={errors.email}
            disabled={loading}
            data-cy="edit-user-email"
          />
          {errors.email && <FieldError>{errors.email}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label>Full Name</Label>
          <Input
            type="text"
            value={formData.full_name}
            onChange={(e) => handleChange('full_name', e.target.value)}
            placeholder="Enter full name"
            error={errors.full_name}
            disabled={loading}
            data-cy="edit-user-fullname"
          />
          {errors.full_name && <FieldError>{errors.full_name}</FieldError>}
        </FormGroup>

        <FormGroup>
          <Label>Role</Label>
          <Select
            value={formData.role}
            onChange={(e) => handleChange('role', e.target.value)}
            disabled={loading || (isEditingSelf && formData.role === 'super_admin')}
            data-cy="edit-user-role"
          >
            {ROLES.map(role => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </Select>
          {isEditingSelf && formData.role === 'super_admin' && (
            <HelpText>You cannot change your own super admin role</HelpText>
          )}
        </FormGroup>

        <FormGroup>
          <Label>Avatar Color</Label>
          <ColorPickerWrapper>
            <ColorPreview color={formData.avatar_color}>
              {getInitials()}
            </ColorPreview>
            <ColorInput
              type="color"
              value={formData.avatar_color}
              onChange={(e) => handleChange('avatar_color', e.target.value)}
              disabled={loading}
              data-cy="edit-user-avatar-color"
            />
            <RandomColorButton
              type="button"
              onClick={generateRandomColor}
              disabled={loading}
            >
              <RefreshCw size={16} />
              Random
            </RandomColorButton>
          </ColorPickerWrapper>
        </FormGroup>

        <ActiveToggleWrapper>
          <div>
            <Label style={{ marginBottom: 0 }}>Account Status</Label>
            <HelpText>{formData.is_active ? 'User can log in' : 'User cannot log in'}</HelpText>
          </div>
          <ToggleSwitch>
            <ToggleInput
              type="checkbox"
              checked={formData.is_active}
              onChange={(e) => handleChange('is_active', e.target.checked)}
              disabled={loading}
              data-cy="edit-user-active-toggle"
            />
            <Slider />
          </ToggleSwitch>
        </ActiveToggleWrapper>

        <ToggleSection>
          <ToggleHeader
            type="button"
            onClick={() => setShowPasswordSection(!showPasswordSection)}
            disabled={loading}
          >
            <ToggleTitle>Change Password (Optional)</ToggleTitle>
            {showPasswordSection ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </ToggleHeader>
          {showPasswordSection && (
            <ToggleContent>
              <FormGroup style={{ marginBottom: '1rem' }}>
                <Label>New Password</Label>
                <PasswordInputWrapper>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => handleChange('password', e.target.value)}
                    placeholder="Enter new password"
                    error={errors.password}
                    disabled={loading}
                    data-cy="edit-user-new-password"
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </PasswordToggle>
                </PasswordInputWrapper>
                {errors.password && <FieldError>{errors.password}</FieldError>}
                <HelpText>Min 8 chars, include uppercase, lowercase, number, and special character</HelpText>
              </FormGroup>

              <FormGroup style={{ marginBottom: 0 }}>
                <Label>Confirm New Password</Label>
                <PasswordInputWrapper>
                  <Input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => handleChange('confirmPassword', e.target.value)}
                    placeholder="Confirm new password"
                    error={errors.confirmPassword}
                    disabled={loading}
                    data-cy="edit-user-confirm-password"
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                  >
                    {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </PasswordToggle>
                </PasswordInputWrapper>
                {errors.confirmPassword && <FieldError>{errors.confirmPassword}</FieldError>}
              </FormGroup>
            </ToggleContent>
          )}
        </ToggleSection>

        <ButtonGroup>
          <CancelButton type="button" onClick={onClose} disabled={loading}>
            Cancel
          </CancelButton>
          <SaveButton
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            data-cy="edit-user-submit"
          >
            {loading ? (
              <>
                <RefreshCw size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Changes
              </>
            )}
          </SaveButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );

  return ReactDOM.createPortal(modalContent, document.body);
}

export default EditUserModal;

